import React, { useEffect } from 'react';
import {BrowserRouter, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import { PageTitleManager } from './hooks/usePageTitle';
import { markAppLoaded } from './utils/appRecovery';
import { useHtmlLangDir } from './i18n/useHtmlLangDir';
import { useSyncMomentLocale } from './i18n/useSyncMomentLocale';
import {AnimatePresence} from 'framer-motion';
import AnimatedPage from "./AnimatePage";
import Sidebar from './components/sidebar/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './auth/login/Login';
import ForgotPassword from './auth/forgoutpassword/ForgotPassword';
import Dashboard from './components/Activite/ActivityPage';
import CataloguePage from './components/CatalogPage/CatalogPage';
import NewCategoryForm from "./components/CatalogPage/category/NewCategoryForm";
import SelectAccount from "./auth/accounts/SelectAccount";
import EditCategoryForm from "./components/CatalogPage/category/EditCategoryForm";
import NewRemiseForm from "./components/CatalogPage/discount/NewRemiseForm";
import EditDiscountForm from "./components/CatalogPage/discount/EditDiscountForm";
import NewBrandForm from "./components/CatalogPage/brand/NewBrandForm";
import EditBrandForm from "./components/CatalogPage/brand/EditBrandForm";
import NewProductForm from "./components/CatalogPage/product/NewProductForm";
import EditProductForm from "./components/CatalogPage/product/EditProductForm";
import NewVarianteProductForm from "./components/CatalogPage/product/NewVarianteProductForm";
import EditVarianteProductForm from "./components/CatalogPage/product/EditVarianteProductForm";
import ClientsTable from "./ClientsTable/ClientsTable";
import ClientNewForm from "./ClientsTable/ClientNewForm";
import ClientEditForm from "./ClientsTable/ClientEditForm";
import Settings from "./settings/Settings";
import StatistiquesClients from "./ClientsTable/StatistiquesClients";
import Stock from "./stock/Stock";
import Calander from "./calander/Calander";
import ChangePassword from "./auth/changepassword/ChangePassword";
import ChangePasswordLoggedIn from "./auth/changepassword/ChangePasswordLoggedIn";
import TechnicalFilesPageWrapper from "./pages/TechnicalFilesPage";
import MarketingPage from "./components/Marketing/MarketingPage";
import LoyaltyPage from "./components/Loyalty/LoyaltyPage";
import AttendancePage from "./components/Attendance/AttendancePage";
import ReturnsPage from "./components/Returns/ReturnsPage";
import PosPage from "./components/POS/PosPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import {AppleToaster} from "./components/Common/Notification";
import StorefrontPage from "./pages/StorefrontPage";
import MarketplacePage from "./components/Marketplace/MarketplacePage";

const withAnimation = (Component) => (props) => (
    <AnimatedPage>
        <Component {...props} />
    </AnimatedPage>
);

// Only animate these standalone pages
const AnimatedLogin = withAnimation(Login);
const AnimatedForgotPassword = withAnimation(ForgotPassword);
const AnimatedSelectAccount = withAnimation(SelectAccount);

const AnimatedRoutes = () => {
    const location = useLocation();

    return (
        <>
            <PageTitleManager />
            <AnimatePresence>
            <Routes location={location} key={location.pathname}>
                {/* Public routes - with animation wrapper */}
                <Route path="/login" element={<AnimatedLogin/>}/>
                <Route path="/forgoutPassword" element={<AnimatedForgotPassword/>}/>
                <Route path="/select-account" element={<AnimatedSelectAccount/>}/>
                <Route path="/changePassword" element={<ChangePassword/>}/>

                {/* Protected routes with Sidebar layout - animation handled by Sidebar */}
                <Route element={<ProtectedRoute/>}>
                    <Route element={<Sidebar/>}>
                        <Route path="/activite" element={<Dashboard/>}/>
                        <Route path="/catalogue" element={<CataloguePage/>}/>
                        <Route path="/NewCategoryForm" element={<NewCategoryForm/>}/>
                        <Route path="/catalogue/edit/:categoryId" element={<EditCategoryForm/>}/>
                        <Route path="/NewRemiseForm" element={<NewRemiseForm/>}/>
                        <Route path="/catalogue/discount/edit/:discountId" element={<EditDiscountForm/>}/>
                        <Route path="/NewBrandForm" element={<NewBrandForm/>}/>
                        <Route path="/catalogue/brand/edit/:brandId" element={<EditBrandForm/>}/>
                        <Route path="/NewProductForm" element={<NewProductForm/>}/>
                        <Route path="/catalogue/product/edit/:productId" element={<EditProductForm/>}/>
                        <Route path="/catalogue/products/:productId/variants/new"
                               element={<NewVarianteProductForm/>}
                        />
                        <Route path="/catalogue/products/edit/:productId/variants/:variantId"
                               element={<EditVarianteProductForm/>}
                        />
                        <Route path="/clients" element={<ClientsTable/>}/>
                        <Route path="/clients/new" element={<ClientNewForm/>}/>
                        <Route path="/clients/edit/:clientId" element={<ClientEditForm/>}/>
                        <Route path="/clients/stats/:clientId" element={<StatistiquesClients/>}/>
                        <Route path="/settings" element={<Settings/>}/>
                        <Route path="/settings/change-password" element={<ChangePasswordLoggedIn/>}/>
                        <Route path="/stock" element={<Stock/>}/>
                        <Route path="/calander" element={<Calander />}/>
                        <Route path="/technical-files" element={<TechnicalFilesPageWrapper />}/>
                        <Route path="/marketing" element={<MarketingPage />}/>
                        <Route path="/fidelite" element={<LoyaltyPage />}/>
                        <Route path="/pointage" element={<AttendancePage />}/>
                        <Route path="/retours" element={<ReturnsPage />}/>
                        <Route path="/caisse" element={<PosPage />}/>
                        <Route path="/integrations" element={<IntegrationsPage />}/>
                        <Route path="/storefront" element={<StorefrontPage />}/>
                        <Route path="/marketplace" element={<MarketplacePage />}/>
                        <Route path="/inventaire" element={<Navigate to="/stock" replace />}/>
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to="/login" replace/>}/>
            </Routes>
            </AnimatePresence>
        </>
    );
};

function App() {
    // Tell the white-screen detector the app loaded successfully
    useEffect(() => {
        markAppLoaded();
    }, []);

    useHtmlLangDir();
    useSyncMomentLocale();

    return (
        <BrowserRouter>
            <AnimatedRoutes/>
            <AppleToaster/>
        </BrowserRouter>
    );
}

export default App;
